import { syncBookingFromWebSocket } from "@/hooks/queries/use-bookings-query";
import {
  ensureEchoConnectionBound,
  isEchoConnected,
  subscribeEchoConnection,
} from "@/lib/echo-connection";
import { queryClient } from "@/lib/query-client";
import { notificationFallbackSync } from "@/lib/services/notification-fallback-sync";
import {
  BookingLiveUpdatedEvent,
  BookingLocationEvent,
} from "@/lib/types/websocket";
import { useBookingJobStore } from "@/stores/use-booking-job-store";
import { useLocationStore } from "@/stores/use-location-store";
import type Echo from "laravel-echo";
import { IMessage } from "react-native-gifted-chat";
import { getEcho } from "../echo";
import { syncQuote } from "@/hooks/queries/use-quotes-query";
import { Quote } from "./quotes";

type BookingLiveListener = (data: BookingLiveUpdatedEvent) => void;

class WebSocketService {
  public echoRef: Echo<"reverb"> | null = null;
  public bookingId: number | null = null;
  public channelName: string | null = null;
  private listeners = new Set<(data: IMessage) => void>();
  private bookingLiveListeners = new Set<BookingLiveListener>();
  private connectionUnsubscribe: (() => void) | null = null;

  connect(bookingId: number) {
    this.bookingId = bookingId;
    this.echoRef = getEcho();

    if (!this.echoRef || !this.bookingId) {
      console.error("No booking ID available for WebSocket connection");
      return;
    }

    ensureEchoConnectionBound(this.echoRef);

    if (!this.connectionUnsubscribe) {
      this.connectionUnsubscribe = subscribeEchoConnection({
        onConnected: () => {
          notificationFallbackSync.sync().catch(() => {});
          notificationFallbackSync.stopBookingPolling();

          if (this.bookingId) {
            this.subscribeToBookingChannel();
          }
        },
        onDisconnected: () => {
          if (this.bookingId) {
            notificationFallbackSync
              .startBookingPolling(this.bookingId)
              .catch(() => {});
          }
        },
      });
    }

    if (isEchoConnected(this.echoRef)) {
      this.subscribeToBookingChannel();
    }

    return true;
  }

  private subscribeToBookingChannel(): void {
    const { setDriverCoordinate } = useLocationStore.getState();
    const { setNewMessage } = useBookingJobStore.getState();

    if (!this.echoRef || !this.bookingId) {
      return;
    }

    if (this.channelName) {
      this.echoRef.leave(this.channelName);
    }

    this.channelName = `bookings.${this.bookingId}`;

    this.echoRef
      .private(this.channelName)
      .listen(".BookingLiveUpdated", (data: BookingLiveUpdatedEvent) => {
        if (data.booking) {
          syncBookingFromWebSocket(queryClient, data.booking);
        }
        this.bookingLiveListeners.forEach((listener) => listener(data));
      })
      .listen(".NewQuoteReceived", (data: BookingLiveUpdatedEvent) => {
        const quotePayload = data?.quote as Quote | { data: Quote } | undefined;
        const quote =
          quotePayload && "data" in quotePayload
            ? quotePayload.data
            : quotePayload;

        if (quote) {
          syncQuote(queryClient, quote);
        }

        this.bookingLiveListeners.forEach((listener) => listener(data));
      })
      .listen(".BookingLocation", (data: BookingLocationEvent) => {
        setDriverCoordinate({
          latitude: data.latitude,
          longitude: data.longitude,
          speed: data.speed || 0,
          heading: data.heading || 0,
        });
      })
      .listen(".MessageSent", (data: IMessage) => {
        this.listeners.forEach((listener) => listener(data));
        setNewMessage(true);
      });
  }

  disconnect(): void {
    notificationFallbackSync.stopBookingPolling();

    if (this.echoRef && this.channelName) {
      this.echoRef.leave(this.channelName);
      this.channelName = null;
    }

    this.echoRef = null;
    this.bookingId = null;

    if (this.connectionUnsubscribe) {
      this.connectionUnsubscribe();
      this.connectionUnsubscribe = null;
    }
  }

  sendMessage(
    type: "update_location" | "chat",
    data: Record<string, unknown>,
  ) {
    if (this.echoRef && isEchoConnected(this.echoRef) && this.channelName) {
      if (type === "update_location") {
        this.echoRef.connector.pusher.send_event(
          "BookingLocation",
          data,
          this.channelName,
        );
      }
      if (type === "chat") {
        this.echoRef.connector.pusher.send_event(
          "MessageSent",
          data,
          this.channelName,
        );
      }
      return true;
    }
    return false;
  }

  subscribe(listener: (data: IMessage) => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  subscribeBookingLive(listener: BookingLiveListener) {
    this.bookingLiveListeners.add(listener);
    return () => {
      this.bookingLiveListeners.delete(listener);
    };
  }
}

export const webSocketService = new WebSocketService();
